import React, { useState, useCallback } from 'react';
import { playbookApi } from '../../api/playbookApi';
import type { PlaybookTransitionValidator } from '../../types/playbook.types';
import { ValidatorConfigForm } from './config-forms/ValidatorConfigForms';

interface ValidatorListPanelProps {
  transitionId: string;
  validators: PlaybookTransitionValidator[];
  onRefresh: () => void;
}

const VALIDATOR_TYPES = [
  'field_required', 'deadline_not_passed', 'form_complete',
  'document_exists', 'expression', 'custom',
];

const ValidatorListPanel: React.FC<ValidatorListPanelProps> = ({ transitionId, validators, onRefresh }) => {
  const [addingType, setAddingType] = useState<string | null>(null);
  const [newConfig, setNewConfig] = useState<Record<string, unknown>>({});
  const [newErrorMsg, setNewErrorMsg] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editConfig, setEditConfig] = useState<Record<string, unknown>>({});
  const [editErrorMsg, setEditErrorMsg] = useState('');

  const handleAdd = useCallback(async () => {
    if (!addingType) return;
    try {
      await playbookApi.addValidator(transitionId, {
        validator_type: addingType,
        configuration: newConfig,
        error_message: newErrorMsg || undefined,
        sort_order: validators.length,
      });
      setAddingType(null);
      setNewConfig({});
      setNewErrorMsg('');
      onRefresh();
    } catch { /* silent */ }
  }, [transitionId, addingType, newConfig, newErrorMsg, validators.length, onRefresh]);

  const handleUpdate = useCallback(async (validatorId: string) => {
    try {
      await playbookApi.updateValidator(transitionId, validatorId, {
        configuration: editConfig,
        error_message: editErrorMsg || undefined,
      });
      setEditingId(null);
      onRefresh();
    } catch { /* silent */ }
  }, [transitionId, editConfig, editErrorMsg, onRefresh]);

  const handleRemove = useCallback(async (validatorId: string) => {
    try {
      await playbookApi.removeValidator(transitionId, validatorId);
      onRefresh();
    } catch { /* silent */ }
  }, [transitionId, onRefresh]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h5 className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Validators ({validators.length})</h5>
        {!addingType && (
          <select
            className="rounded border border-gray-300 px-1 py-0.5 text-[10px] dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
            value=""
            onChange={(e) => { setAddingType(e.target.value); setNewConfig({}); }}
          >
            <option value="">+ Add validator</option>
            {VALIDATOR_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
      </div>

      {validators.map((v) => (
        <div key={v.id} className="rounded border border-gray-200 dark:border-gray-700 p-2" style={{ animation: 'fadeSlideIn 200ms ease-out' }}>
          <div className="flex items-center justify-between mb-1">
            <span className="rounded-full bg-teal-50 px-2 py-0.5 text-[9px] font-medium text-teal-700 dark:bg-teal-900/30 dark:text-teal-300">
              {v.validator_type}
            </span>
            <div className="flex gap-1">
              <button type="button" onClick={() => { setEditingId(v.id === editingId ? null : v.id); setEditConfig(v.configuration); setEditErrorMsg(v.error_message ?? ''); }} className="text-[9px] text-lex-accent hover:text-lex-accent-hover">
                {editingId === v.id ? 'Close' : 'Edit'}
              </button>
              <button type="button" onClick={() => handleRemove(v.id)} className="text-[9px] text-rose-500 hover:text-rose-700">Remove</button>
            </div>
          </div>
          {v.error_message && <p className="text-[9px] text-gray-400 mb-1">Error: {v.error_message}</p>}
          {editingId === v.id && (
            <div className="mt-2 space-y-2 border-t border-gray-100 dark:border-gray-700 pt-2">
              <ValidatorConfigForm validatorType={v.validator_type} config={editConfig} onChange={setEditConfig} />
              <div>
                <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase mb-0.5">Error Message</label>
                <input type="text" className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300" value={editErrorMsg} onChange={(e) => setEditErrorMsg(e.target.value)} />
              </div>
              <button type="button" onClick={() => handleUpdate(v.id)} className="rounded bg-lex-accent px-3 py-1 text-[10px] font-medium text-lex-accent-contrast hover:bg-lex-accent-hover">Save</button>
            </div>
          )}
        </div>
      ))}

      {addingType && (
        <div className="rounded border border-teal-200 dark:border-teal-700 p-2 bg-teal-50/30 dark:bg-teal-900/10" style={{ animation: 'coverageDrillDown 200ms ease-out' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-medium text-teal-600 dark:text-teal-400">New: {addingType}</span>
            <button type="button" onClick={() => setAddingType(null)} className="text-[9px] text-gray-400 hover:text-gray-600">Cancel</button>
          </div>
          <ValidatorConfigForm validatorType={addingType} config={newConfig} onChange={setNewConfig} />
          <div className="mt-2">
            <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase mb-0.5">Error Message</label>
            <input type="text" className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300" value={newErrorMsg} onChange={(e) => setNewErrorMsg(e.target.value)} />
          </div>
          <button type="button" onClick={handleAdd} className="mt-2 rounded bg-teal-600 px-3 py-1 text-[10px] font-medium text-white hover:bg-teal-700">Add Validator</button>
        </div>
      )}
    </div>
  );
};

export default ValidatorListPanel;
